import 'client-only';

import { useEffect } from 'react';

import { type Brand } from '@/consts/brands';
import { type AnalyticsPatientResource } from '@/types/metrics/analyticsPatient';

declare global {
  interface Window {
    CUROLOGY_ENV?: {
      APP_BRAND?: string;
    };
    duet?: {
      setMetadata: (metadata: {
        email?: string;
        name?: string;
        path?: string;
      }) => void;
      setUserId: (userId: string) => void;
      updateStyle: (style: { [key: string]: string | number }) => void;
    };
  }
}

const decagonIframeLoaded = (
  brand: string | null | undefined,
  callback: () => void,
) => {
  if (typeof document === 'undefined' || !brand) {
    return () => undefined;
  }

  const MAX_ATTEMPTS = 60;
  const BASE_DELAY_MS = 50;
  const MAX_DELAY_MS = 2000;

  let timeoutId: number | undefined;
  let iframe: Element | null = null;
  let attempts = 0;
  const handler = () => callback();

  const tryAttachListener = () => {
    attempts += 1;
    iframe = document.querySelector(`[src="https://decagon.ai/demo/${brand}"]`);

    if (iframe instanceof HTMLIFrameElement) {
      if (isIframeLoaded(iframe)) {
        callback();
      } else {
        iframe.addEventListener('load', handler, { once: true });
      }
      return;
    }

    if (attempts >= MAX_ATTEMPTS) {
      console.warn(
        '[useDecagonChatBot] Decagon iframe not found after polling; giving up.',
      );
      return;
    }

    const delay = Math.min(
      BASE_DELAY_MS * 2 ** Math.max(attempts - 1, 0),
      MAX_DELAY_MS,
    );
    timeoutId = window.setTimeout(tryAttachListener, delay);
  };

  tryAttachListener();

  return () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    if (iframe instanceof HTMLIFrameElement) {
      iframe.removeEventListener('load', handler);
    }
  };
};

const scheduleWhenIdle = (
  win: Window & typeof globalThis,
  callback: () => void,
  timeout: number,
) => {
  // Dispatch work when the main thread is idle so we do not block navigation.
  if (typeof win.requestIdleCallback === 'function') {
    const idleHandle = win.requestIdleCallback(
      () => {
        callback();
      },
      { timeout },
    );

    return () => {
      if (typeof win.cancelIdleCallback === 'function') {
        win.cancelIdleCallback(idleHandle);
      }
    };
  }

  const timeoutHandle = win.setTimeout(callback, 0);

  return () => {
    win.clearTimeout(timeoutHandle);
  };
};

const isIframeLoaded = (iframe: HTMLIFrameElement) => {
  try {
    return iframe.contentWindow?.document?.readyState === 'complete';
  } catch (error) {
    // Cross-origin iframe access throws; fall back to waiting for load event.
    if (process.env.NODE_ENV !== 'production') {
      console.debug(
        '[useDecagonChatBot] Unable to inspect Decagon iframe state.',
        error,
      );
    }
    return false;
  }
};

const hasDecagonScript = () =>
  Boolean(document.getElementById('decagon-script'));

const appendDecagonScript = (brandName: string, onLoad: () => void) => {
  const script = document.createElement('script');
  script.setAttribute('id', 'decagon-script');
  script.src = `https://decagon.ai/loaders/${brandName}.js`;
  script.async = true;
  script.addEventListener('load', onLoad, { once: true });
  script.addEventListener(
    'error',
    (error) => {
      console.error(
        '[useDecagonChatBot] Failed to load Decagon chatbot script.',
        error,
      );
    },
    { once: true },
  );
  document.body.appendChild(script);
};

const updateDuetMetadata = (
  win: Window & typeof globalThis,
  patient?: AnalyticsPatientResource,
) => {
  win.duet?.setMetadata({
    email: patient?.email,
    name: patient?.fullName,
    path: win.location.pathname,
  });

  if (patient) {
    win.duet?.setUserId(String(patient.id));
  }

  win.duet?.updateStyle({
    zIndex: 999994,
  });
};

export const useDecagonChatBot = (
  brand?: Brand,
  patient?: AnalyticsPatientResource,
) => {
  const brandName = brand ?? window?.CUROLOGY_ENV?.APP_BRAND ?? patient?.brand;

  useEffect(() => {
    if (!brandName || typeof document === 'undefined') {
      return;
    }

    const win = window as Window & typeof globalThis;
    let cancelled = false;
    let cleanupIframeListener: (() => void) | undefined;

    // Keep metadata in sync without forcing the chat widget into the critical path.
    const applyMetadata = () => {
      if (cancelled) {
        return;
      }

      updateDuetMetadata(win, patient);
    };

    const ensureScriptLoaded = () => {
      if (cancelled) {
        return;
      }

      cleanupIframeListener?.();
      cleanupIframeListener = decagonIframeLoaded(brandName, applyMetadata);

      if (hasDecagonScript()) {
        applyMetadata();
        return;
      }

      appendDecagonScript(brandName, applyMetadata);
    };

    if (win.duet) {
      applyMetadata();
      cleanupIframeListener = decagonIframeLoaded(brandName, applyMetadata);
      return () => {
        cancelled = true;
        cleanupIframeListener?.();
      };
    }

    // Load the third-party widget after the initial interaction budget.
    const cleanupIdle = scheduleWhenIdle(win, ensureScriptLoaded, 4000);

    return () => {
      cancelled = true;
      cleanupIframeListener?.();
      cleanupIdle();
    };
  }, [brandName, patient]);
};
